import { Component, computed, ElementRef, HostListener, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { ROUTES } from '../../../core/constants/routes.constants';
import { AuthService } from '../../../auth/services/auth.service';
import { ThemeService } from '../../../core/services/theme.service';
import { NotificationService } from '../../../core/services/notification.service';
import { SidebarService } from '../../../core/services/sidebar.service';
import { LanguageService } from '../../../core/services/language.service';

@Component({
  selector: 'app-admin-header',
  imports: [RouterLink, TranslatePipe],
  templateUrl: './admin-header.component.html',
  styleUrl: './admin-header.component.scss'
})
export class AdminHeaderComponent {
  private readonly router = inject(Router);
  private readonly elRef = inject(ElementRef);
  protected readonly authService = inject(AuthService);
  protected readonly themeService = inject(ThemeService);

  protected readonly routes = ROUTES;

  protected readonly islamicDate = {
    hijri: "12 Dhul Qa'dah 1445",
    gregorian: 'May 20, 2024',
    weekday: 'Monday'
  };

  protected readonly sidebarService = inject(SidebarService);
  protected readonly notificationService = inject(NotificationService);
  protected readonly languageService = inject(LanguageService);
  protected readonly userMenuOpen = signal(false);
  protected readonly themeMenuOpen = signal(false);
  protected readonly notifMenuOpen = signal(false);
  protected readonly langMenuOpen = signal(false);

  protected readonly userInitials = computed(() => {
    const name = this.authService.userName();
    if (!name) return 'G';

    const words = name.trim().split(/\s+/);
    return words.length > 1 ? (words[0][0] + words[words.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.notifMenuOpen.set(false);
      this.themeMenuOpen.set(false);
      this.userMenuOpen.set(false);
      this.langMenuOpen.set(false);
    }
  }

  toggleSidebar(event: Event): void {
    event.stopPropagation();
    if (window.innerWidth <= 900) {
      this.sidebarService.toggleDrawer();
    } else {
      this.sidebarService.toggleCollapsed();
    }
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update((open) => !open);
    this.themeMenuOpen.set(false);
    this.notifMenuOpen.set(false);
  }

  toggleThemeMenu(): void {
    this.themeMenuOpen.update((open) => !open);
    this.userMenuOpen.set(false);
    this.notifMenuOpen.set(false);
  }

  toggleNotifMenu(): void {
    this.notifMenuOpen.update((open) => !open);
    this.userMenuOpen.set(false);
    this.themeMenuOpen.set(false);
  }

  toggleLangMenu(): void {
    this.langMenuOpen.update(o => !o);
    this.themeMenuOpen.set(false);
    this.notifMenuOpen.set(false);
    this.userMenuOpen.set(false);
  }

  selectTheme(id: string): void {
    this.themeService.setTheme(id);
    this.themeMenuOpen.set(false);
  }

  selectLanguage(code: string): void {
    this.languageService.setLanguage(code);
    this.langMenuOpen.set(false);
  }

  logout(): void {
    this.userMenuOpen.set(false);
    this.authService.logout().subscribe(() => {
      this.router.navigateByUrl(ROUTES.HOME);
    });
  }
}
